import { Fragment, useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { getSolutionTree } from "@/api/solutionApi";
import { getProductCategories, getProducts } from "@/api/productApi";
import { getCaseCategories, getCases } from "@/api/caseApi";
import {
  getAccentColor,
  getCategoryNavDisplay,
  getCaseCategoryStyle,
  CASE_TO_SOLUTION_CATEGORY,
  DEFAULT_CATEGORY_ICON,
  AI_BUSINESS_CATEGORY_ID,
  getCategoryEntryHref,
} from "@/utils/solutionDisplay";
import { useCompanyInfo } from "@/hooks/useCompanyInfo";
import { assetUrl } from "@/utils/assetUrl";
import { ACTIVE_CATEGORY_SET, buildSolutionsTitle } from "@/utils/solutionCategory";
import { takeVisibleSubSolutions, shouldShowExpandToggle } from "@/utils/solutionList";

/** Nav_Dropdown 分类简介单行截断的最大字符数（超过则以省略号截断，Requirement 6.2） */
const CATEGORY_DESC_MAX_LENGTH = 40;
/** GET /api/front/solutions 请求超时时间（毫秒），超时按失败处理（Requirement 6.11） */
const SOLUTIONS_FETCH_TIMEOUT_MS = 5000;

interface NavLink {
  label: string;
  path: string;
  hasDropdown?: boolean;
  dropdownType?: "solutions" | "products" | "cases";
}

const navLinks: NavLink[] = [
  { label: "主页", path: "/" },
  { label: "解决方案", path: "/solutions", hasDropdown: true, dropdownType: "solutions" },
  { label: "产品", path: "/products", hasDropdown: true, dropdownType: "products" },
  { label: "成功案例", path: "/cases", hasDropdown: true, dropdownType: "cases" },
  { label: "关于我们", path: "/about" },
  { label: "行业资讯", path: "/news" },
];

// 产品下拉数据：以接口返回的解决方案分类为分组（按 sort 升序），
// 通过 subSolution.relatedProductIds → 产品 → 产品.category（即产品类型 id）
// 组装出：{ [l1]: [{ typeId, typeName, products: Product[] }...] }
/** 产品下拉的一列：一个解决方案分类及其展示信息 */
interface ProductNavCategory {
  id: string;
  name: string;
  icon: string;
  desc: string;
  accent: string;
  light: string;
}

function toNavCategory(id: string, raw?: any): ProductNavCategory {
  const d = getCategoryNavDisplay(id, { name: raw?.tag || raw?.title, desc: raw?.subtitle, icon: raw?.icon });
  return { id, name: d.name, icon: d.icon, desc: d.desc, accent: getAccentColor(id), light: d.light };
}

interface ProductTypeGroup {
  typeId: string;
  typeName: string;
  products: { id: number; name: string }[];
}

const useProductsByL1 = (enabled: boolean) => {
  const [map, setMap] = useState<Record<string, ProductTypeGroup[]>>({});
  // 接口返回前先用生效分类集合占位，保证下拉不会出现空白列；返回后以接口数据（含顺序）为准
  const [navCategories, setNavCategories] = useState<ProductNavCategory[]>(() =>
    ACTIVE_CATEGORY_SET.map((id) => toNavCategory(id)),
  );

  useEffect(() => {
    if (!enabled) return;
    Promise.all([
      getProductCategories(),
      getProducts(),
      getSolutionTree(),
    ])
      .then(([catRes, products, tree]: any[]) => {
        const l1s = (catRes?.l1Categories || []).filter(
          (l1: any) => (l1.id ?? l1.categoryId) !== "all",
        );
        // 产品类型 id → 名称
        const typeIdToName = new Map<string, string>();
        l1s.forEach((l1: any) => {
          const key = String(l1.categoryId ?? l1.id);
          typeIdToName.set(key, l1.name);
        });

        // 产品 id → 产品
        const productById = new Map<number, any>();
        (products || []).forEach((p: any) => productById.set(Number(p.id), p));

        // 解决方案分类 → 关联的产品 id 集合；分类集合与顺序完全来自接口返回（按 sort 升序）
        const sortedTree: any[] = [...(tree || [])].sort((a: any, b: any) => (a.sort ?? 0) - (b.sort ?? 0));
        const l1ToProductIds: Record<string, Set<number>> = {};
        sortedTree.forEach((cat: any) => {
          l1ToProductIds[cat.categoryId ?? cat.id] = new Set();
        });
        sortedTree.forEach((cat: any) => {
          const l1Key = cat.categoryId ?? cat.id;
          const bucket = l1ToProductIds[l1Key];
          if (!bucket) return;
          (cat.subSolutions || []).forEach((sub: any) => {
            const ids: any[] = sub.relatedProductIds ?? sub.relatedProducts ?? [];
            ids.forEach((id: any) => {
              const n = Number(id);
              if (!Number.isNaN(n)) bucket.add(n);
            });
          });
        });

        // 按 L1 → 产品类型 分组
        const result: Record<string, ProductTypeGroup[]> = {};
        Object.entries(l1ToProductIds).forEach(([l1Key, idSet]) => {
          const groupByType = new Map<string, ProductTypeGroup>();
          idSet.forEach((pid) => {
            const product = productById.get(pid);
            if (!product) return;
            const typeId = String(product.category ?? "");
            if (!typeId) return;
            if (!groupByType.has(typeId)) {
              groupByType.set(typeId, {
                typeId,
                typeName: typeIdToName.get(typeId) || typeId,
                products: [],
              });
            }
            groupByType.get(typeId)!.products.push({ id: Number(product.id), name: product.name });
          });
          result[l1Key] = Array.from(groupByType.values());
        });
        setMap(result);
        if (sortedTree.length > 0) {
          setNavCategories(sortedTree.map((cat: any) => toNavCategory(cat.categoryId ?? cat.id, cat)));
        }
      })
      .catch(() => {});
  }, [enabled]);

  return { navCategories, map };
};

// 获取案例分类数据（从API加载）
const useCaseCategories = (enabled: boolean) => {
  const [caseCategories, setCaseCategories] = useState<any[]>([]);
  useEffect(() => {
    if (!enabled) return;
    Promise.all([getCaseCategories(), getCases({ pageNum: 1, pageSize: 50 })]).then(([catRes, casesRes]: any[]) => {
      const cats = (Array.isArray(catRes) ? catRes : []).filter((c: any) => c.id !== "all");
      const allCases = casesRes.rows || [];
      setCaseCategories(cats.map((cat: any) => ({
        id: cat.id,
        name: cat.name,
        cases: allCases.filter((c: any) => c.solution === cat.id).slice(0, 5),
      })));
    }).catch(() => {});
  }, [enabled]);
  return caseCategories;
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [solutionCategories, setSolutionCategories] = useState<any[]>([]);
  const [solutionsError, setSolutionsError] = useState(false);
  // 标记本次请求是否已经返回过一次结果（成功或失败），用于区分「加载中」与
  // 「已加载但返回空列表」（Requirement 6.13：空列表时不展开面板）
  const [solutionsLoaded, setSolutionsLoaded] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  // 下拉数据懒加载开关：用户首次 hover 对应菜单后才置为 true，避免首屏拉取
  const [productsTouched, setProductsTouched] = useState(false);
  const [casesTouched, setCasesTouched] = useState(false);
  const [solutionsTouched, setSolutionsTouched] = useState(false);
  const location = useLocation();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { navCategories: productNavCategories, map: productsByL1 } = useProductsByL1(productsTouched);
  const caseCategories = useCaseCategories(casesTouched);
  const companyInfo = useCompanyInfo();

  const toggleCategory = (id: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const isHomePage = location.pathname === "/";

  // 请求 GET /api/front/solutions：底层 cachedRequest 已在同一次页面加载内做并发去重与
  // 成功结果缓存（失败不缓存，见 src/api/cache.ts），这里额外叠加 5 秒超时：
  // 超时或失败时不展示分类区块、置错误态，供下一次 hover 重新发起请求。
  const fetchSolutions = () => {
    setSolutionsError(false);
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error("solutions_fetch_timeout")), SOLUTIONS_FETCH_TIMEOUT_MS);
    });
    Promise.race([getSolutionTree(), timeoutPromise])
      .then((res: any) => {
        const data = Array.isArray(res) ? res : [];
        setSolutionCategories(data);
        setSolutionsError(false);
        setSolutionsLoaded(true);
        // 默认展开全部分类
        const allIds = new Set(data.map((s: any) => s.categoryId || s.id));
        setExpandedCategories(allIds);
      })
      .catch(() => {
        setSolutionCategories([]);
        setSolutionsError(true);
        setSolutionsLoaded(true);
      });
  };

  // 解决方案下拉数据：仅在用户首次 hover「解决方案」菜单后加载（同一次页面加载仅发起一次请求）
  useEffect(() => {
    if (!solutionsTouched) return;
    fetchSolutions();
  }, [solutionsTouched]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollThreshold = isHomePage ? window.innerHeight * 0.85 : 60;
      setScrolled(window.scrollY > scrollThreshold);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path) && path !== "/";
  };

  const isTransparent = isHomePage && !scrolled;

  const handleMouseEnter = (dropdownType: string) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // 首次 hover 时触发对应下拉数据的懒加载
    if (dropdownType === "solutions") {
      if (!solutionsTouched) setSolutionsTouched(true);
      // 上一次请求失败（不缓存失败态）时，下一次 hover 重新发起请求
      else if (solutionsError) fetchSolutions();
    } else if (dropdownType === "products") setProductsTouched(true);
    else if (dropdownType === "cases") setCasesTouched(true);
    setActiveDropdown(dropdownType);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const handleDropdownMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setActiveDropdown(null), 150);
  };

  const closeDropdown = () => {
    setActiveDropdown(null);
    // 重置为全部展开
    const allIds = new Set(solutionCategories.map((s: any) => s.categoryId || s.id));
    setExpandedCategories(allIds);
  };

  // ── 解决方案下拉菜单 ──────────────────────────────────────────────────────
  // 请求失败/超时：不展示分类区块，展示加载失败提示，保留「查看全部方案」入口
  const renderSolutionsErrorDropdown = () => (
    <div
      className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
      style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
      onMouseEnter={handleDropdownMouseEnter}
      onMouseLeave={handleDropdownMouseLeave}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex flex-col items-center justify-center text-center gap-3">
        <p className="text-sm text-gray-400">解决方案分类加载失败，请稍后重试</p>
        <Link
          to="/solutions"
          onClick={closeDropdown}
          className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
        >
          查看全部方案
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  );

  // 空列表：不展开面板（父菜单本身仍可点击进入 /solutions，见 navLinks 渲染）
  const renderSolutionsDropdown = () => {
    if (solutionsError) return renderSolutionsErrorDropdown();
    if (solutionsLoaded && solutionCategories.length === 0) return null;

    return (
      <div
        className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          {/* 顶部标题行 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">Solutions</p>
              <h3 className="text-base font-bold text-[#0d1f3c]">{buildSolutionsTitle(solutionCategories.length)}</h3>
            </div>
            <Link
              to="/solutions"
              onClick={closeDropdown}
              className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
            >
              查看全部方案
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* 分类区块栅格：≥1024px 每行4列，768-1024px 每行2列 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 items-start">
            {solutionCategories.map((solution) => {
              const catId = solution.categoryId || solution.id;
              const accentColor = getAccentColor(catId);
              const displayName = solution.title || solution.name;
              const displayDesc: string = solution.desc || solution.description || "";
              const truncatedDesc = displayDesc.length > CATEGORY_DESC_MAX_LENGTH
                ? `${displayDesc.slice(0, CATEGORY_DESC_MAX_LENGTH)}...`
                : displayDesc;
              const displayIcon = solution.icon || DEFAULT_CATEGORY_ICON;
              const isExpanded = expandedCategories.has(catId);
              const subSolutions: any[] = solution.subSolutions || [];
              const visibleSubSolutions = takeVisibleSubSolutions(subSolutions, isExpanded);
              const showExpandToggle = shouldShowExpandToggle(subSolutions);
              return (
                <div key={solution.id} className="group">
                  {/* 大类标题卡片 */}
                  <Link
                    to={getCategoryEntryHref(catId, `/solutions?category=${catId}`)}
                    onClick={closeDropdown}
                    className="flex items-center gap-3 p-3 rounded-xl mb-3 transition-all duration-200 hover:bg-gray-50"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-200"
                      style={{ backgroundColor: `${accentColor}1a` }}
                    >
                      <i className={`${displayIcon} text-base`} style={{ color: accentColor }}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0d1f3c] group-hover:text-[#1a56db] transition-colors">{displayName}</span>
                        <i className="ri-arrow-right-line text-xs text-gray-300 group-hover:text-[#1a56db] group-hover:translate-x-0.5 transition-all duration-200"></i>
                      </div>
                      {truncatedDesc && <p className="text-xs text-gray-400 truncate mt-0.5">{truncatedDesc}</p>}
                    </div>
                  </Link>

                  {/* 分割线 */}
                  <div className="h-px mx-3 mb-3" style={{ backgroundColor: `${accentColor}15` }}></div>

                  {/* 子方案列表 - 两列布局，默认最多6条（3行×2列） */}
                  <div className="px-1">
                    <div className="grid grid-cols-2 gap-x-2">
                      {visibleSubSolutions.map((sub: any) => (
                        <Link
                          key={sub.id}
                          to={`/solutions/detail/${sub.id}`}
                          onClick={closeDropdown}
                          className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-[#0d1f3c] hover:bg-gray-50 transition-all duration-150 group/item whitespace-nowrap overflow-hidden"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0 transition-all duration-150 group-hover/item:scale-125"
                            style={{ backgroundColor: accentColor, opacity: 0.4 }}
                          ></span>
                          <span className="truncate">{sub.name}</span>
                        </Link>
                      ))}
                    </div>

                    {showExpandToggle && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleCategory(catId);
                        }}
                        className="w-full flex items-center justify-center gap-1 mt-2 py-1.5 text-xs text-gray-400 hover:text-[#1a56db] transition-colors cursor-pointer"
                      >
                        {isExpanded ? (
                          <>
                            收起
                            <i className="ri-arrow-up-s-line text-sm"></i>
                          </>
                        ) : (
                          <>
                            展开全部（共{subSolutions.length}个）
                            <i className="ri-arrow-down-s-line text-sm"></i>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── 产品下拉菜单（一级为接口返回的解决方案分类）──────────────────────────────
  const renderProductsDropdown = () => {
    return (
      <div
        className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          {/* 顶部标题行 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">Products</p>
              <h3 className="text-base font-bold text-[#0d1f3c]">全系列产品</h3>
            </div>
            <Link
              to="/products"
              onClick={closeDropdown}
              className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
            >
              查看全部产品
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* 分类列 — 数量与顺序随解决方案分类变化 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {productNavCategories.map((solution) => {
              const color = { accent: solution.accent, light: solution.light };
              const groups = productsByL1[solution.id] || [];

              return (
                <div key={solution.id}>
                  {/* 解决方案标题 */}
                  <Link
                    to={getCategoryEntryHref(solution.id, `/products?solution=${solution.id}`)}
                    onClick={closeDropdown}
                    className="flex items-center gap-3 p-3 rounded-xl mb-3 transition-all duration-200 hover:bg-gray-50 group"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color.light }}
                    >
                      <i className={`${solution.icon} text-base`} style={{ color: color.accent }}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0d1f3c] group-hover:text-[#1a56db] transition-colors">{solution.name}</span>
                        <i className="ri-arrow-right-line text-xs text-gray-300 group-hover:text-[#1a56db] group-hover:translate-x-0.5 transition-all duration-200"></i>
                      </div>
                      {solution.desc && <p className="text-xs text-gray-400 truncate mt-0.5">{solution.desc}</p>}
                    </div>
                  </Link>

                  {/* 分割线 */}
                  <div className="h-px mx-3 mb-3" style={{ backgroundColor: `${color.accent}15` }}></div>

                  {/* 产品类型 + 每类型下的产品 */}
                  <div className="space-y-2 px-1">
                    {groups.length === 0 && (
                      <p className="px-2 py-1 text-xs text-gray-300">暂无产品</p>
                    )}
                    {groups.slice(0, 5).map((g) => (
                      <div key={g.typeId}>
                        <Link
                          to={`/products?solution=${solution.id}&category=${g.typeId}`}
                          onClick={closeDropdown}
                          className="flex items-center gap-1.5 px-2 py-1 text-xs font-semibold text-gray-600 hover:text-[#0d1f3c] transition-colors rounded-md hover:bg-gray-50"
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: color.accent }}
                          ></span>
                          {g.typeName}
                        </Link>
                        {g.products.length > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 pl-5 mt-0.5">
                            {g.products.slice(0, 4).map((p) => (
                              <Link
                                key={p.id}
                                to={`/products/${p.id}`}
                                onClick={closeDropdown}
                                className="text-xs text-gray-400 hover:text-[#1a56db] transition-colors py-0.5 truncate max-w-[180px]"
                                title={p.name}
                              >
                                {p.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ── 案例下拉菜单 ──────────────────────────────────────────────────────────
  const renderCasesDropdown = () => {
    return (
      <div
        className="absolute top-full left-0 right-0 bg-white border-t-2 border-[#1a56db]/20"
        style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.10)" }}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
          {/* 顶部标题行 */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-gray-400 font-medium tracking-wider uppercase mb-1">Cases</p>
              <h3 className="text-base font-bold text-[#0d1f3c]">成功案例</h3>
            </div>
            <Link
              to="/cases"
              onClick={closeDropdown}
              className="flex items-center gap-1.5 text-xs text-[#1a56db] hover:gap-2.5 transition-all duration-200 font-medium"
            >
              查看全部案例
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          {/* 分类列 — 与案例分类接口返回一致 */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {caseCategories.map((category) => {
              // 案例分类标识 → 方案分类标识（基础架构 → it-infrastructure，其余同名），取名称/图标/简介
              const cfg = getCategoryNavDisplay(CASE_TO_SOLUTION_CATEGORY[category.id] ?? category.id, { name: category.name });
              const color = { accent: getCaseCategoryStyle(category.id).hex, light: cfg.light };
              const displayName = cfg.name || category.name;
              const displayIcon = cfg.icon;
              return (
                <div key={category.id}>
                  {/* 大类标题 */}
                  <Link
                    to={getCategoryEntryHref(CASE_TO_SOLUTION_CATEGORY[category.id] ?? category.id, `/cases?solution=${category.id}`)}
                    onClick={closeDropdown}
                    className="flex items-center gap-3 p-3 rounded-xl mb-3 transition-all duration-200 hover:bg-gray-50 group"
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: color.light }}
                    >
                      <i className={`${displayIcon} text-base`} style={{ color: color.accent }}></i>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#0d1f3c] group-hover:text-[#1a56db] transition-colors">{displayName}</span>
                        <span
                          className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                          style={{ backgroundColor: color.light, color: color.accent }}
                        >
                          {category.cases.length}
                        </span>
                        <i className="ri-arrow-right-line text-xs text-gray-300 group-hover:text-[#1a56db] group-hover:translate-x-0.5 transition-all duration-200"></i>
                      </div>
                      {cfg.desc && <p className="text-xs text-gray-400 truncate mt-0.5">{cfg.desc}</p>}
                    </div>
                  </Link>

                  {/* 分割线 */}
                  <div className="h-px mx-3 mb-3" style={{ backgroundColor: `${color.accent}15` }}></div>

                  {/* 案例列表 */}
                  <ul className="space-y-0.5 px-1">
                    {category.cases.length === 0 && (
                      <li className="px-2 py-1 text-xs text-gray-300">暂无案例</li>
                    )}
                    {category.cases.map((caseItem) => (
                      <li key={caseItem.id}>
                        <Link
                          to={`/cases/${caseItem.id}`}
                          onClick={closeDropdown}
                          className="flex items-start gap-2 px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:text-[#0d1f3c] hover:bg-gray-50 transition-all duration-150 group/item"
                        >
                          <span
                            className="w-1 h-1 rounded-full flex-shrink-0 mt-1.5 transition-all duration-150 group-hover/item:scale-150"
                            style={{ backgroundColor: color.accent, opacity: 0.4 }}
                          ></span>
                          <span className="line-clamp-1">{caseItem.title}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <header
      data-site-navbar
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
        isTransparent
          ? "bg-transparent"
          : "bg-white/95 backdrop-blur-md border-b border-gray-100/50 shadow-sm"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center justify-between h-16 lg:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0">
          {(isTransparent ? (companyInfo.logoDark || companyInfo.logo) : companyInfo.logo) ? (
            <img
              src={assetUrl(isTransparent ? (companyInfo.logoDark || companyInfo.logo) : companyInfo.logo)}
              alt={companyInfo.companyName}
              className="h-8 max-w-[160px] object-contain"
            />
          ) : (
            <>
              <div
                className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-500 ${
                  isTransparent ? "bg-white/20" : "bg-[#1a56db]"
                }`}
              >
                <i className="ri-shield-check-line text-lg text-white"></i>
              </div>
              <span className={`text-sm font-bold hidden sm:inline transition-colors duration-500 ${isTransparent ? "text-white" : "text-[#0d1f3c]"}`}>
                {companyInfo.companyName}
              </span>
            </>
          )}
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center justify-center absolute left-1/2 -translate-x-1/2 gap-1">
          {navLinks.map((link) => (
            <div
              key={link.path}
              className="relative"
              onMouseEnter={() => link.hasDropdown && link.dropdownType && handleMouseEnter(link.dropdownType)}
              onMouseLeave={handleMouseLeave}
            >
              <Link
                to={link.path}
                className={`px-3 lg:px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 whitespace-nowrap flex items-center gap-1 ${
                  isActive(link.path)
                    ? isTransparent
                      ? "text-white bg-white/20"
                      : "text-[#1a56db] bg-[#1a56db]/8"
                    : isTransparent
                      ? "text-white hover:text-white hover:bg-white/10"
                      : "text-[#333333] hover:text-[#1a56db] hover:bg-[#1a56db]/5"
                }`}
              >
                {link.label}
                {link.hasDropdown && (
                  <i
                    className={`ri-arrow-down-s-line text-xs transition-transform duration-200 ${
                      activeDropdown === link.dropdownType ? "rotate-180" : ""
                    }`}
                  ></i>
                )}
              </Link>
            </div>
          ))}
        </nav>

        {/* CTA Button（暂时隐藏，改用爱番番第三方客服） */}
        {/* <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => window.dispatchEvent(new Event("open-contact-hub"))}
            className={`px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer ${
              isTransparent
                ? "bg-white text-[#0d1f3c] hover:bg-white/90 shadow-lg"
                : "bg-[#1a56db] hover:bg-[#1e40af] text-white shadow-md hover:shadow-lg"
            }`}
          >
            免费咨询
          </button>
        </div> */}

        {/* Mobile Menu Button */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
            isTransparent ? "text-white" : "text-[#333333]"
          }`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </div>

      {/* Dropdown Menus */}
      <div>
        {activeDropdown === "solutions" && renderSolutionsDropdown()}
        {activeDropdown === "products" && renderProductsDropdown()}
        {activeDropdown === "cases" && renderCasesDropdown()}
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 shadow-lg">
          {navLinks.map((link) => (
            <Fragment key={link.path}>
              <Link
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`block py-3 text-sm border-b border-gray-50 last:border-0 ${
                  isActive(link.path) ? "text-[#1a56db] font-medium" : "text-[#333333] hover:text-[#1a56db]"
                }`}
              >
                {link.label}
              </Link>
              {/* 解决方案 / 产品 / 成功案例分组下的「AI业务」子入口，统一进入 AI业务首页 */}
              {link.hasDropdown && (
                <Link
                  to={getCategoryEntryHref(AI_BUSINESS_CATEGORY_ID, link.path)}
                  onClick={() => setMenuOpen(false)}
                  className="block py-2.5 pl-6 text-sm border-b border-gray-50 text-gray-500 hover:text-[#1a56db]"
                >
                  AI业务
                </Link>
              )}
            </Fragment>
          ))}
          {/* 免费咨询按钮暂时隐藏，改用爱番番第三方客服 */}
          {/* <button
            onClick={() => { setMenuOpen(false); window.dispatchEvent(new Event("open-contact-hub")); }}
            className="mt-4 block w-full text-center px-5 py-2.5 bg-[#1a56db] text-white text-sm font-semibold rounded-full cursor-pointer"
          >
            免费咨询
          </button> */}
        </div>
      )}
    </header>
  );
}
